import { useState, useEffect } from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import Header from '../Header';
import SimulationForm from './SimulationForm';
import SimulationResult from './SimulationResult';
import { useTranslation } from '../../hooks/useTranslation';
import { useNavigate } from 'react-router';
import { tokens } from '../../theme';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { HOST_API } from '../../config/hosts';
import { formatCurrency, reconcileInstallmentValues } from '../../utils';

const EMPTY_RESALE = { valueInstallment: 0, totalPaid: 0, iofValue: 0, operationValue: 0 };

const SimulatorRedrural = ({ simulation, customerId, simulatorConfig }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Estado da simulação ───────────────────────────────────────────────────
  const [loadingSimulate, setLoadingSimulate] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulationPayload, setSimulationPayload] = useState(null);
  const [needsResimulate, setNeedsResimulate] = useState(false);

  const [activeTab, setActiveTab] = useState('simulation');
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const isRedutoOrigin = simulation?.type === 'REDUTO_CAPITAL';

  // Quando a origem é REDUTO, os valores de revenda devem ser zerados,
  // pois o contrato foi (ou será) firmado exclusivamente pelo Reduto Capital.
  const applyRedutoRule = (data, isReduto) => {
    if (!isReduto) return data;
    return { ...data, resale: EMPTY_RESALE };
  };

  useEffect(() => {
    if (!simulation) return;

    const isFixed = simulation.payment_type === 'parcelamento_fixo';
    const isReduto = simulation.type === 'REDUTO_CAPITAL';

    const payload = {
      customer_id: simulation.customer_id,
      client_name: simulation.client_name,
      client_document: simulation.client_document,
      total_value: simulation.total_value,
      entry_value: simulation.entry_value,
      financed_value: simulation.financed_value,
      interest_rate: simulation.interest_rate,
      iof_rate: simulation.iof_rate,
      tac_rate: simulation.tac_rate,
      operational_fee: simulation.operational_fee,
      payment_type: simulation.payment_type,
      installments: simulation.installments,
      first_due_date: isFixed
        ? null
        : (simulation.custom_dates?.[0]?.date ?? simulation.first_due_date ?? null),
      custom_dates: simulation.custom_dates?.length
        ? simulation.custom_dates.map((d) => ({
            date: typeof d.date === 'string' ? d.date.split('T')[0] : d.date,
            value: parseFloat(d.value),
          }))
        : null,
      has_custom_installment: simulation.has_custom_installment ?? false,
      documents: simulation.custom_fields?.documents ?? [],
    };

    const calculate = async () => {
      setLoadingSimulate(true);
      try {
        const { data } = await api.post('/v1/simulation/calculate', payload);

        const calculatedResult = applyRedutoRule(data, isReduto);

        const savedInstallmentValue = parseFloat(simulation.installment_value) || 0;
        const savedTotalPaid = parseFloat(simulation.total_paid) || 0;

        const useSaved =
          savedTotalPaid > 0 && (!isFixed || savedInstallmentValue > 0);

        const resultWithSavedValues = useSaved
          ? {
              ...calculatedResult,
              [isReduto ? 'reduto' : 'resale']: {
                ...calculatedResult[isReduto ? 'reduto' : 'resale'],
                valueInstallment: savedInstallmentValue,
                totalPaid: savedTotalPaid,
              },
            }
          : calculatedResult;

        setSimulationResult(resultWithSavedValues);
        setSimulationPayload(payload);
      } catch {
        toast.error(t('messages.errorRequestSimulation'));
      } finally {
        setLoadingSimulate(false);
      }
    };

    calculate();
  }, [simulation?.id]);

  const handleSimulate = async (payload) => {
    setLoadingSimulate(true);
    setNeedsResimulate(false);

    try {
      const { data } = await api.post('/v1/simulation/calculate', payload);
      setSimulationResult(applyRedutoRule(data, isRedutoOrigin));
      setSimulationPayload(payload);
    } catch (error) {
      toast.error(t('messages.errorRequestSimulation'));
    } finally {
      setLoadingSimulate(false);
    }
  };

  const handleSubmitByType = async (type) => {
    if (!simulationPayload || !simulationResult) return;

    if (simulation && simulation.type !== type) {
      toast.error(
        simulation.type === 'REVENDA'
          ? t('messages.errorSimulatioByResale')
          : t('messages.errorSimulatioByReduto')
      );
      return;
    }

    const isReduto = type === 'REDUTO_CAPITAL';

    if (isReduto && simulatorConfig) {
      const totalValue = parseFloat(simulationPayload.total_value) || 0;

      if (simulatorConfig.min_value) {
        const min = parseFloat(simulatorConfig.min_value);
        if (totalValue < min) {
          toast.error(t('validation.totalBelowMin', { min: formatCurrency(min) }));
          return;
        }
      }

      if (simulatorConfig.max_value) {
        const max = parseFloat(simulatorConfig.max_value);
        if (totalValue > max) {
          toast.error(t('validation.totalAboveMax', { max: formatCurrency(max) }));
          return;
        }
      }
    }

    const result = isReduto ? simulationResult.reduto : simulationResult.resale;

    let customDatesForSave = simulationPayload.custom_dates;
    if (
      simulationPayload.payment_type === 'data_personalizada' &&
      Array.isArray(customDatesForSave) &&
      customDatesForSave.length > 0
    ) {
      customDatesForSave = reconcileInstallmentValues(customDatesForSave, result.totalPaid);
    }

    const jsonPayload = {
      ...simulationPayload,
      custom_dates: customDatesForSave,
      type,
      requested_origin: type,
      installment_value: result.valueInstallment,
      total_paid: result.totalPaid,
      iof_rate_calculated: result.iofValue,
    };

    const { documents, ...rest } = jsonPayload;
    const existingSaved = (documents || []).filter((d) => !(d instanceof File));
    const newFiles = (documents || []).filter((d) => d instanceof File);
    const hasNewFiles = newFiles.length > 0;

    try {
      setLoadingSubmit(true);

      if (hasNewFiles) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(rest));
        formData.append('existingDocuments', JSON.stringify(existingSaved));
        newFiles.forEach((file) => formData.append('documents', file));

        if (simulation) {
          await api.put(`${HOST_API}/v1/simulation/${simulation.id}`, formData);
        } else {
          await api.post(`${HOST_API}/v1/simulation`, formData);
        }
      } else {
        const payload = { ...rest, existingDocuments: existingSaved };

        if (simulation) {
          const { customer_id, client_name, client_document, ...editPayload } = payload;
          await api.put(`${HOST_API}/v1/simulation/${simulation.id}`, editPayload);
        } else {
          await api.post(`${HOST_API}/v1/simulation`, payload);
        }
      }

      toast.success(
        simulation ? t('messages.simulationEditSuccess') : t('messages.simulationRequestedSuccess')
      );
      navigate(-1);
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      const apiMessage = error?.response?.data?.message;

      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        apiErrors.forEach((msg) => {
          toast.error(typeof msg === 'string' ? msg : msg?.message || JSON.stringify(msg));
        });
      } else if (apiErrors && typeof apiErrors === 'object') {
        Object.values(apiErrors)
          .flat()
          .forEach((err) => {
            toast.error(typeof err === 'string' ? err : err?.message || JSON.stringify(err));
          });
      } else {
        toast.error(apiMessage || t('messages.errorRequestSimulation'));
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  const isPaymentMethodFixed = simulationPayload?.payment_type === 'parcelamento_fixo';
  const installments = simulationPayload?.installments ?? 0;
  const customDateInstallmentsCount = simulationPayload?.custom_dates?.length ?? 0;
  const clientId = simulationPayload?.customer_id ?? simulation?.customer_id;

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, minHeight: '100vh' }}>
      <Button
        onClick={() => navigate(-1)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: colors.greenAccent[500],
          textTransform: 'none',
          padding: 0,
          marginBottom: 3,
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: '32px', mr: 1 }} />
        <Typography fontSize={16} fontWeight={400}>
          {t('common.back')}
        </Typography>
      </Button>

      <Header
        title={t('titles.redruralSimulation')}
        subtitle={t('subTitles.simulation')}
        Icon={DescriptionIcon}
        iconBackground
      />

      <SimulationForm
        simulation={simulation}
        customerId={customerId}
        simulatorConfig={simulatorConfig}
        onSimulate={handleSimulate}
        loadingSimulate={loadingSimulate}
        onDataChange={() => {
          if (simulationResult) setNeedsResimulate(true);
        }}
      />

      {simulationResult && (
        <SimulationResult
          result={simulationResult}
          simulationPayload={simulationPayload}
          isPaymentMethodFixed={isPaymentMethodFixed}
          installments={installments}
          customDateInstallmentsCount={customDateInstallmentsCount}
          loadingSubmit={loadingSubmit}
          onSubmitByType={handleSubmitByType}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          clientId={clientId}
          isRedutoOrigin={isRedutoOrigin}
          disableResale={isRedutoOrigin}
          needsResimulate={needsResimulate}
        />
      )}
    </Box>
  );
};

export default SimulatorRedrural;
